<template>
  <modal name="update-agreement" class="modal-inner modal-update-agreement" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true">
    <span class="close-button" @click="hide('update-agreement')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span v-if="updateInfo">{{ updateInfo.agreement_number }}</span>
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formUpdateAgreement" v-if="updateInfo != null">
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.agreement_type" :options="agreement_types" nameSelector="name" optionSelector="id" :field_name="$t('agreement_create.agreement_type')" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.agreement_number" type="text" :field_name="$t('agreement_create.agreement_number')" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.e_bill" type="text" :field_name="$t('agreement_create.e_invoice')" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2 mb-4">
            {{ $t("agreement_update_modal.lessee") }}
          </div>
        </div>

        <!-- Koppla befintlig kund – only shown for temporary agreements -->
        <div class="flex flex-wrap" v-if="updateInfo.status === 'temporary'">
          <div class="w-full md:w-1/2 px-2 py-2">
            <div v-if="updateInfo.customer_id">
              <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t('agreement_create.customer') }}</label>
              <div class="mt-2 flex items-center bg-green-50 border border-green-200 rounded px-3 py-2">
                <span class="text-sm flex-1">{{ selectedCustomerNameEdit }}</span>
                <button @click.prevent="clearCustomerSelectionEdit()" class="text-red-400 hover:text-red-600 ml-2 text-xs font-bold">✕</button>
              </div>
            </div>
            <div v-else class="relative">
              <BaseInput
                v-model="customerSearchQuery"
                type="text"
                :field_name="$t('agreement_create.customer')"
                :placeholder="$t('create_temporary_agreement.customer_search_placeholder')"
              />
              <p v-if="customerSearchQuery.length > 0 && customerSearchQuery.length < 4" class="text-xs text-gray-400 mt-1">
                {{ $t('create_temporary_agreement.search_min_chars') }}
              </p>
              <div v-if="customerSearchQuery.length >= 4" class="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg max-h-52 overflow-y-auto mt-1">
                <div v-if="filteredCustomersEdit.length === 0" class="px-3 py-2 text-gray-500 text-sm">
                  {{ $t('create_temporary_agreement.no_results') }}
                </div>
                <div
                  v-for="c in filteredCustomersEdit"
                  :key="c._id"
                  class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-0"
                  @click="selectCustomerEdit(c)"
                >
                  {{ c.name }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.customer_name" type="text" :field_name="$t('agreement_create.name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.company_number" type="text" :field_name="$t('agreement_create.org_number')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.customer_phone" type="text" :field_name="$t('agreement_create.phone')" rules="required" />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.billing_adress" type="text" :field_name="$t('agreement_create.invoice_address')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.billing_zip_code" type="text" :field_name="$t('agreement_create.zip_code')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.billing_city" type="text" :field_name="$t('agreement_create.city')" rules="required" />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.customer_contact" type="text" :field_name="$t('agreement_create.contact_person')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.customer_reference" type="text" :field_name="$t('agreement_create.ref')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2 mb-4">
            {{ $t("agreement_update_modal.lessor") }}
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_name" type="text" :field_name="$t('agreement_update_modal.name')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_company_number" type="text" :field_name="$t('agreement_update_modal.org_number')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_phone" type="text" :field_name="$t('agreement_update_modal.phone')" readonly disabled />
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_address" type="text" :field_name="$t('agreement_update_modal.address')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_zip_code" type="text" :field_name="$t('agreement_update_modal.zip_code')" readonly disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.dealer_city" type="text" :field_name="$t('agreement_update_modal.ort')" readonly disabled />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.seller" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('agreement_update_modal.seller')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 px-2 py-2">
            <BaseTextarea rows="5" v-model="updateInfo.units_specification" :field_name="$t('agreement_update_modal.object_specification')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <ValidationProvider :name="$t('agreement_update_modal.contract_start')" rules="required" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <label class="text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("agreement_update_modal.contract_start") }}<b class="text-red-500 ml-1">*</b></label>
                <date-picker :lang="datePickerLocale" :key="date_picker_langKey" class="mt-2" v-model="updateInfo.contract_start" value-type="format" format="YYYY-MM-DD"></date-picker>
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.status !== 'temporary'">
            <BaseSelect v-model="updateInfo.sim_card" :options="sim_operators" :field_name="$t('agreement_create.sim_card')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.status !== 'temporary'">
            <BaseSelect v-model="updateInfo.alarm_center" :options="[...alarm_centers, { _id: '-', name: $t('agreement_create.none') }]" nameSelector="name" optionSelector="_id" :field_name="$t('agreement_create.alarm_center')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap mt-4">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.agreement_category" :options="agreementCategoryOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.agreement_category')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="insuranceValue" :options="insuranceOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.insurance')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full px-2">
            <div class="flex flex-col">
              <div class="w-full text-gray-600 text-sm font-bold pb-3 font-serif">
                {{ $t('admin_order_alarm.product') }}
              </div>
              
              <!-- Hierarchical Category/Subcategory/Product Display -->
              <div class="space-y-2">
                <div v-for="category in categoriesWithProducts" :key="category.value" class="border border-gray-200 rounded">
                  <!-- Category Header -->
                  <div 
                    class="px-4 py-2 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100"
                    @click="toggleCategory(category.value)"
                  >
                    <div class="flex items-center">
                      <span class="text-sm font-semibold text-gray-800">{{ category.label }}</span>
                      <span class="ml-2 text-xs text-gray-500">({{ groupedProducts[category.value].length }})</span>
                      <span v-if="updateInfo.status === 'pending' && categoryProductCount(category.value) > 0" class="ml-1 text-xs font-semibold text-green-600">{{ categoryProductCount(category.value) }}</span>
                    </div>
                    <span class="text-gray-500 text-xs">{{ expandedCategories[category.value] ? '▲' : '▼' }}</span>
                  </div>
                  
                  <!-- Category Content -->
                  <div v-show="expandedCategories[category.value]" class="bg-white">
                    
                    <!-- Subcategories -->
                    <div v-if="subcategoriesByParent[category.value] && subcategoriesByParent[category.value].length > 0">
                      <div v-for="subcategory in subcategoriesByParent[category.value]" :key="subcategory._id" class="border-b border-gray-100">
                        <!-- Subcategory Header -->
                        <div 
                          class="px-4 py-2 pl-8 bg-blue-50 cursor-pointer flex justify-between items-center hover:bg-blue-100"
                          @click="toggleSubcategory(subcategory._id)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-blue-800">{{ subcategory.displayName }}</span>
                            <span class="ml-2 text-xs text-blue-600">({{ getProductsForSubcategory(category.value, subcategory._id).length }})</span>
                            <span v-if="updateInfo.status === 'pending' && subcategoryProductCount(category.value, subcategory._id) > 0" class="ml-1 text-xs font-semibold text-green-600">{{ subcategoryProductCount(category.value, subcategory._id) }}</span>
                          </div>
                          <span class="text-blue-500 text-xs">{{ expandedSubcategories[subcategory._id] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <!-- Subcategory Products -->
                        <div v-show="expandedSubcategories[subcategory._id]" class="pl-8 pr-2 py-2 space-y-2">
                          <div v-for="p in getProductsForSubcategory(category.value, subcategory._id)" :key="p.variant_id ? `${p._id}-${p.variant_id}` : p._id">
                            <!-- Product Row -->
                            <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <!-- Variant Rows -->
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p._id}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Products Without Subcategory -->
                      <div v-if="getProductsWithoutSubcategory(category.value).length > 0" class="border-b border-gray-100">
                        <div 
                          class="px-4 py-2 pl-8 bg-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-200"
                          @click="toggleSubcategory(`${category.value}-none`)"
                        >
                          <div class="flex items-center">
                            <span class="text-sm font-medium text-gray-600">{{ $t('products.without_subcategory') }}</span>
                            <span class="ml-2 text-xs text-gray-500">({{ getProductsWithoutSubcategory(category.value).length }})</span>
                          </div>
                          <span class="text-gray-500 text-xs">{{ expandedSubcategories[`${category.value}-none`] ? '▲' : '▼' }}</span>
                        </div>
                        
                        <div v-show="expandedSubcategories[`${category.value}-none`]" class="pl-8 pr-2 py-2 space-y-2">
                          <div v-for="p in getProductsWithoutSubcategory(category.value)" :key="p.variant_id ? `${p._id}-${p.variant_id}` : p._id">
                            <!-- Product Row -->
                            <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                              <div class="w-2/5 text-sm flex items-center">
                                <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                                  {{ expandedVariants[p._id] ? '▼' : '▶' }}
                                </button>
                                <div class="flex-1">
                                  <template v-if="p.display_name">
                                    <div class="font-semibold">{{ p.display_name }}</div>
                                    <div class="text-xs text-gray-500">{{ p.name }}</div>
                                  </template>
                                  <template v-else>
                                    <span class="font-medium">{{ p.name }}</span>
                                  </template>
                                  <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                                </div>
                              </div>
                              <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                                <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                              </template>
                              <template v-else>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                                </div>
                              </template>
                            </div>
                            <!-- Variant Rows -->
                            <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                              <div v-for="variant in p.variants" :key="`${p._id}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                                <div class="w-2/5 text-sm pl-2 flex items-center">
                                  <span class="text-blue-500 mr-2">↳</span>
                                  <div>
                                    <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                    <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                                  </div>
                                </div>
                                <div class="w-1/5 px-2">
                                  <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                                  <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                                <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                                  <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- No Subcategories: Show products directly -->
                    <div v-else class="px-4 py-2 space-y-2">
                      <div v-for="p in groupedProducts[category.value]" :key="p.variant_id ? `${p._id}-${p.variant_id}` : p._id">
                        <!-- Product Row -->
                        <div class="flex flex-wrap items-center bg-gray-50 rounded p-2">
                          <div class="w-2/5 text-sm flex items-center">
                            <button v-if="p.hasVariants && p.variants && p.variants.length > 0" @click.stop="toggleVariants(p._id)" class="mr-2 text-gray-500 hover:text-gray-700 text-xs">
                              {{ expandedVariants[p._id] ? '▼' : '▶' }}
                            </button>
                            <div class="flex-1">
                              <template v-if="p.display_name">
                                <div class="font-semibold">{{ p.display_name }}</div>
                                <div class="text-xs text-gray-500">{{ p.name }}</div>
                              </template>
                              <template v-else>
                                <span class="font-medium">{{ p.name }}</span>
                              </template>
                              <div v-if="p.hasVariants && p.variants && p.variants.length > 0" class="text-xs text-blue-600">{{ p.variants.length }} varianter</div>
                            </div>
                          </div>
                          <template v-if="p.hasVariants && p.variants && p.variants.length > 0">
                            <div class="w-3/5 px-2 text-xs text-gray-500 italic">{{ $t('admin_order_alarm.select_variant_to_order') }}</div>
                          </template>
                          <template v-else>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="p.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="p.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_rental_price > 0 ? 'Rek: ' + p.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="p.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="p.recommended_cash_price > 0 ? 'Rek: ' + p.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-gray-300 px-2 py-1" />
                            </div>
                          </template>
                        </div>
                        <!-- Variant Rows -->
                        <template v-if="expandedVariants[p._id] && p.variants && p.variants.length > 0">
                          <div v-for="variant in p.variants" :key="`${p._id}-${variant._id}`" class="flex flex-wrap items-center bg-blue-50 rounded p-2 ml-6 border-l-2 border-blue-300">
                            <div class="w-2/5 text-sm pl-2 flex items-center">
                              <span class="text-blue-500 mr-2">↳</span>
                              <div>
                                <span class="text-gray-700 font-medium">{{ variant.name }}</span>
                                <div class="text-xs text-gray-500">{{ variant.articleNumber || '-' }}</div>
                              </div>
                            </div>
                            <div class="w-1/5 px-2">
                              <input type="number" v-model.number="variant.qty" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" placeholder="Antal" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowRentalForProduct(p)">
                              <input type="number" v-model.number="variant.rental_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_rental_price > 0 ? 'Rek: ' + variant.recommended_rental_price : 'Hyra'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                            <div class="w-1/5 px-2" v-if="shouldShowCashForProduct(p)">
                              <input type="number" v-model.number="variant.cash_price" min="0" @keypress="isNumber($event)" @blur="calculateTotalPrice()" :placeholder="variant.recommended_cash_price > 0 ? 'Rek: ' + variant.recommended_cash_price : 'Kontant'" class="bg-white rounded w-full text-sm text-gray-900 focus:outline-none border border-blue-300 px-2 py-1" />
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.agreement_category === 'cash_services'">
            <BaseInput v-model.number="totalCashPrice" @keypress="isNumber($event)" type="text" :field_name="$t('admin_order_alarm.total_cash_price')" disabled />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.contract_period" type="text" :field_name="$t('agreement_create.contract_period')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.setup_cost" type="text" :field_name="$t('agreement_create.setup_cost')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="showRentalPrice">
            <BaseInput v-model="updateInfo.rental_month_cost" type="text" :field_name="$t('agreement_create.rental_cost')" rules="required" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.payment_terms" :options="paymentTermsOptions" nameSelector="name" optionSelector="value" :field_name="$t('agreement_create.payment_terms')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="isPaymentTermsFreetext">
            <BaseInput v-model="updateInfo.payment_terms_freetext" type="text" :field_name="$t('agreement_create.payment_terms_freetext_label')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 px-2 py-2">
            <BaseTextarea rows="5" v-model="updateInfo.special_terms" :field_name="$t('agreement_create.special_terms')" rules="required" />
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 px-2 py-2">
            <label class="text-gray-600 text-sm font-bold pb-2 font-serif"
              ><span v-if="updateInfo.pdf_file != null">{{ $t("agreement_update_modal.change") }} </span>{{ $t("agreement_update_modal.signed_agreement") }}</label
            >
            <div class="relative">
              <input type="file" accept="application/pdf" ref="pdfCreate" @change="fileChangeCreate()" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
              <div class="bg-gray-100 rounded w-full text-sm text-gray-900 border-b-4 border-gray-300 px-3 pt-2 pb-2 flex items-center">
                <span class="btn-blue-outline px-3 py-1 mr-3">{{ $t("file_input.choose_file") }}</span>
                <span class="text-gray-500">{{ selectedPdfName || $t("file_input.no_file_selected") }}</span>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2 py-2" v-if="updateInfo.pdf_file && !remove_pdf_file">
            <button class="btn-red-outline py-2 mt-7" @click.prevent="removePDF()">{{ $t("agreement_update_modal.delete_pdf") }}<BaseIcon icon="file-pdf" class="ml-1" /></button>
          </div>
        </div>

        <!-- Uploaded Documents Section -->
        <div v-if="hasAnyDocuments" class="mx-2 border-b border-secondary-400 my-5"></div>
        <div v-if="hasAnyDocuments" class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2 mb-4">
            {{ $t("agreement_update_modal.uploaded_documents") || 'Uppladdade dokument' }}
          </div>
        </div>
        <div v-if="hasAnyDocuments" class="flex flex-col gap-3 px-2 mb-4">
          <!-- Insurance Removal PDF -->
          <div v-if="updateInfo.insurance_removal_pdf" class="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <BaseIcon icon="file-pdf" class="text-red-500" />
            <span class="text-sm font-medium">{{ $t('agreement_update_modal.insurance_removal_pdf') || 'Försäkringsuppsägning' }}</span>
            <a :href="updateInfo.insurance_removal_pdf" target="_blank" class="text-blue-600 hover:text-blue-800 text-sm underline">
              {{ $t('agreement_update_modal.view') || 'Visa' }}
            </a>
            <span class="mx-1 text-gray-300">|</span>
            <label class="text-orange-600 hover:text-orange-800 text-sm underline cursor-pointer">
              {{ $t('agreement_update_modal.replace') || 'Ersätt' }}
              <input type="file" accept="application/pdf" class="hidden" @change="replacePdf('insurance_removal', 0, $event)" />
            </label>
          </div>
          <!-- Cancellation PDFs -->
          <template v-if="updateInfo.cancellation_info && updateInfo.cancellation_info.length > 0">
            <div v-for="(cancel, idx) in updateInfo.cancellation_info" :key="'cancel-' + idx" class="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <template v-if="cancel.cancellation_pdf">
                <BaseIcon icon="file-pdf" class="text-red-500" />
                <span class="text-sm font-medium">{{ $t('agreement_update_modal.cancellation_pdf') || 'Uppsägningsblankett' }}</span>
                <a :href="cancel.cancellation_pdf" target="_blank" class="text-blue-600 hover:text-blue-800 text-sm underline">
                  {{ $t('agreement_update_modal.view') || 'Visa' }}
                </a>
                <span class="mx-1 text-gray-300">|</span>
                <label class="text-orange-600 hover:text-orange-800 text-sm underline cursor-pointer">
                  {{ $t('agreement_update_modal.replace') || 'Ersätt' }}
                  <input type="file" accept="application/pdf" class="hidden" @change="replacePdf('cancellation', idx, $event)" />
                </label>
              </template>
            </div>
          </template>
          <!-- Dismissal PDFs -->
          <template v-if="updateInfo.dismissal_info && updateInfo.dismissal_info.length > 0">
            <div v-for="(dismiss, idx) in updateInfo.dismissal_info" :key="'dismiss-' + idx" class="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
              <template v-if="dismiss.dismissal_pdf">
                <BaseIcon icon="file-pdf" class="text-red-500" />
                <span class="text-sm font-medium">{{ $t('agreement_update_modal.dismissal_pdf') || 'Avslutsblankett' }}</span>
                <a :href="dismiss.dismissal_pdf" target="_blank" class="text-blue-600 hover:text-blue-800 text-sm underline">
                  {{ $t('agreement_update_modal.view') || 'Visa' }}
                </a>
                <span class="mx-1 text-gray-300">|</span>
                <label class="text-orange-600 hover:text-orange-800 text-sm underline cursor-pointer">
                  {{ $t('agreement_update_modal.replace') || 'Ersätt' }}
                  <input type="file" accept="application/pdf" class="hidden" @change="replacePdf('dismissal', idx, $event)" />
                </label>
              </template>
            </div>
          </template>
        </div>

        <!-- <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full text-sm font-bold italic px-2">
            PDF-inställning
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 px-2 py-2">
            <label class="inline-flex items-center mt-3">
                <input v-model="updateInfo.pdf_hide_alarmcenter" :value="updateInfo.pdf_hide_alarmcenter" type="checkbox" class="form-checkbox h-4 w-4">
                <span class="ml-2 text-sm font-sans font-semibold">Dölj larmcentral</span>
              </label>
          </div>
        </div> -->
      </ValidationObserver>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button v-if="updateInfo && updateInfo.pdf_file == null" class="btn-blue-outline px-3 py-2 float-left" @click="downloadPDF(pdf_agreement)">{{ $t("agreement_update_modal.create_pdf") }}<BaseIcon icon="file-pdf" class="ml-1" /></button>
        <button v-else class="btn-red-outline px-3 py-2 float-left" @click="hide('update-agreement')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-green px-3 py-2 float-right" @click="updateAgreement()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 900;

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/locale/pl";
import "vue2-datepicker/locale/nb";
import "@/utils/datePickerDutch";
import "vue2-datepicker/index.css";
import siteContext from "@/utils/siteContext";
import agreementCostMixin from "@/mixins/agreementCostMixin";

export default {
  siteContext,
  mixins: [agreementCostMixin],
  props: {
    agreementInfo: {
      required: true,
      default: null,
    },
  },
  components: {
    DatePicker,
  },
  data() {
    return {
      user: this.$store.state.user,
      sellers: [],
      alarm_centers: [],
      updateInfo: null,
      pdf_agreement: null,
      selected_pdf_file: null,
      remove_pdf_file: false,
      date_picker_lang: this.$i18n.locale,
      date_picker_langKey: 0,
      // defaultProducts removed - products are now loaded from database via API
      defaultProducts: [],
      productsFromDB: [],
      subcategories: [],
      // country the sellers/alarm centers/subcategories were loaded for
      listsCountry: null,
      expandedCategories: {},
      expandedSubcategories: {},
      expandedVariants: {},
      customers: [],
      customer_units: [],
      customerSearchQuery: '',
    };
  },
  computed: {
    selectedPdfName() {
      return this.selected_pdf_file ? this.selected_pdf_file.name : null;
    },
    hasAnyDocuments() {
      if (!this.updateInfo) return false;
      if (this.updateInfo.insurance_removal_pdf) return true;
      if (this.updateInfo.cancellation_info && this.updateInfo.cancellation_info.some(c => c.cancellation_pdf)) return true;
      if (this.updateInfo.dismissal_info && this.updateInfo.dismissal_info.some(d => d.dismissal_pdf)) return true;
      return false;
    },
    sim_operators() {
      return this.productsFromDB
        .filter(p => p.category === 'simcard_supplier')
        .map(p => p.name);
    },
    categoryOptions() {
      return [
        { value: 'alarm', label: this.$t('sales_dashboard.cat_personal_alarm') },
        { value: 'tag', label: this.$t('sales_dashboard.cat_tags') },
        { value: 'display', label: this.$t('sales_dashboard.cat_display') },
        { value: 'ajax', label: this.$t('sales_dashboard.cat_perimeter') },
        { value: 'vehicle_tracker', label: this.$t('sales_dashboard.cat_vehicle_tracker') },
        { value: 'software', label: this.$t('sales_dashboard.cat_software') },
        { value: 'simcard', label: this.$t('sales_dashboard.cat_simcard') },
        { value: 'accessory', label: this.$t('sales_dashboard.cat_accessory') },
        { value: 'other', label: this.$t('sales_dashboard.cat_other') }
      ];
    },
    agreement_types() {
      return [
        { id: "customer", name: this.$t("agreement_create.customers") },
        { id: "reseller", name: this.$t("agreement_create.resellers") },
      ];
    },
    agreementCategoryOptions() {
      const options = [
        { value: 'rental', name: this.$t('admin_order_alarm.category_rental') },
        { value: 'cash_services', name: this.$t('admin_order_alarm.category_cash_services') },
        { value: 'financed', name: this.$t('admin_order_alarm.category_financed') }
      ];
      // FINU/FINI available for pending agreements, or if already set, or for bypass user (amaper)
      const isBypassUser = this.user && this.user.username === 'amaper';
      if (isBypassUser || (this.updateInfo && (this.updateInfo.status === 'pending' || this.updateInfo.agreement_category === 'financed_umea' || this.updateInfo.agreement_category === 'financed_ikano'))) {
        options.push(
          { value: 'financed_umea', name: this.$t('admin_order_alarm.category_financed_umea') },
          { value: 'financed_ikano', name: this.$t('admin_order_alarm.category_financed_ikano') }
        );
      }
      return options;
    },
    insuranceOptions() {
      return [
        { value: 'yes', name: this.$t('yes') },
        { value: 'no', name: this.$t('no') }
      ];
    },
    insuranceValue: {
      get() {
        if (!this.updateInfo) return 'yes';
        return this.updateInfo.insurance === false ? 'no' : 'yes';
      },
      set(val) {
        if (this.updateInfo) {
          this.updateInfo.insurance = val === 'yes';
        }
      }
    },
    showRentalPrice() {
      return this.updateInfo && ['rental', 'financed', 'financed_umea', 'financed_ikano', 'cash_services'].includes(this.updateInfo.agreement_category);
    },
    showCashPrice() {
      return this.updateInfo && this.updateInfo.agreement_category === 'cash_services';
    },
    visibleProducts() {
      if (!this.updateInfo || !this.updateInfo.products) return [];
      const category = this.updateInfo.agreement_category;
      
      return this.updateInfo.products.filter(p => {
        if (p.always_visible) return true;
        if (category === 'cash_services') return true;
        return true;
      });
    },
    hardwareProducts() {
      return this.visibleProducts.filter(p => !p.is_software);
    },
    softwareProducts() {
      return this.visibleProducts.filter(p => p.is_software);
    },
    totalCashPrice() {
      if (!this.updateInfo || !this.updateInfo.products) return 0;
      return this.updateInfo.products.reduce((total, p) => {
        // For cash_services, exclude monthly products (software, simcard, accessory monthly, other monthly) from cash sum
        if (p.is_software || p.category === 'simcard' || p.category === 'software' || 
            (p.category === 'accessory' && p.type && p.type.includes('monthly')) ||
            (p.category === 'other' && p.type && p.type.includes('monthly'))) {
          return total;
        }
        if (p.qty > 0 && p.cash_price > 0) {
          return total + (p.qty * p.cash_price);
        }
        return total;
      }, 0);
    },
    cashSumForRentalFinanced() {
      // For rental and financed: sum of cash products (accessory/other with 'cash' in type)
      if (!this.updateInfo || !this.updateInfo.products) return 0;
      if (!['rental', 'financed', 'financed_umea', 'financed_ikano'].includes(this.updateInfo.agreement_category)) return 0;
      return this.updateInfo.products.reduce((total, p) => {
        // Check for cash products: accessory or other category with 'cash' in type name
        if (((p.category === 'accessory' || p.category === 'other') && p.type && p.type.includes('cash')) && 
            p.qty > 0 && p.cash_price > 0) {
          return total + (p.qty * p.cash_price);
        }
        return total;
      }, 0);
    },
    paymentTermsOptions() {
      const isNorwegian = this.agreementCountry() === 'no';
      
      if (isNorwegian) {
        return [
          { value: "Årlig", name: this.$t("agreement_create.payment_terms_yearly") },
          { value: "Halvårlig", name: this.$t("agreement_create.payment_terms_half_year") },
          { value: "Kvartalsvis", name: this.$t("agreement_create.payment_terms_quarterly") },
          { value: "Månedlig", name: this.$t("agreement_create.payment_terms_monthly") },
          { value: "__fritekst__", name: this.$t("agreement_create.payment_terms_freetext") },
        ];
      }
      
      return [
        { value: "Årsvis", name: this.$t("agreement_create.payment_terms_yearly") },
        { value: "Halv år", name: this.$t("agreement_create.payment_terms_half_year") },
        { value: "Kvartal", name: this.$t("agreement_create.payment_terms_quarterly") },
        { value: "Månad", name: this.$t("agreement_create.payment_terms_monthly") },
        { value: "__fritext__", name: this.$t("agreement_create.payment_terms_freetext") },
      ];
    },    isPaymentTermsFreetext() {
      return this.updateInfo && (this.updateInfo.payment_terms === '__fritekst__' || this.updateInfo.payment_terms === '__fritext__');
    },    datePickerLocale() {
      // Map 'no' to 'nb' for date picker (Norwegian Bokmål)
      return this.date_picker_lang === 'no' ? 'nb' : this.date_picker_lang;
    },
    // Group products by category
    groupedProducts() {
      if (!this.updateInfo || !this.updateInfo.products) return {};
      const grouped = {};
      this.updateInfo.products.forEach(product => {
        const key = product.category || 'other';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(product);
      });
      return grouped;
    },
    // Get subcategories for a specific category
    subcategoriesByParent() {
      const grouped = {};
      this.subcategories.forEach(sub => {
        if (!grouped[sub.parentCategory]) {
          grouped[sub.parentCategory] = [];
        }
        grouped[sub.parentCategory].push(sub);
      });
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });
      return grouped;
    },
    // Categories that have products
    categoriesWithProducts() {
      return this.categoryOptions.filter(cat => 
        this.groupedProducts[cat.value] && this.groupedProducts[cat.value].length > 0
      );
    },
    selectedCustomerNameEdit() {
      if (!this.updateInfo || !this.updateInfo.customer_id) return '';
      const customer = this.customers.find(c => c._id === this.updateInfo.customer_id);
      return customer ? customer.name : (this.updateInfo.customer_name || '');
    },
    filteredCustomersEdit() {
      if (this.customerSearchQuery.length < 4) return [];
      const q = this.customerSearchQuery.toLowerCase();
      return this.customers
        .filter(c => c.name && c.name.toLowerCase().includes(q))
        .slice(0, 30);
    },
  },
  watch: {
    user() {
      return this.$store.state.user;
    },
    async agreementInfo(newVal) {
      // eslint-disable-line
      this.updateInfo = _.cloneDeep(newVal);
      this.expandedCategories = {};
      this.expandedSubcategories = {};
      this.expandedVariants = {};
      
      // The lists follow the agreement's country - reload them when it differs
      // from the ones loaded (e.g. a Norwegian agreement from the Swedish portal)
      if (this.listsCountry && this.listsCountry !== this.agreementCountry()) {
        this.loadCountryLists();
      }

      // Fetch products from database first to get recommended prices
      await this.fetchProductsFromDB();
      
      // If agreement doesn't have products or has empty products, fetch from order log
      if (!this.updateInfo.products || this.updateInfo.products.length === 0) {
        this.fetchProductsFromOrder(newVal.agreement_number);
      } else {
        this.updateInfo.products = this.mergeProducts(this.updateInfo.products);
        // Pending/temporary: show full catalog so sellers can add products
        // Active/extended/redrawn: only show products already on the agreement
        const editableStatuses = ['temporary', 'pending'];
        if (editableStatuses.includes(this.updateInfo.status)) {
          this.updateInfo.products = this.addMissingCatalogProducts(this.updateInfo.products);
        }
      }
      
      // Set default agreement_category if not set
      if (!this.updateInfo.agreement_category) {
        this.updateInfo.agreement_category = 'rental';
      }
      
      // The PDF shows the agreement as saved - taken before the freetext payment terms
      // below turn into the dropdown's "__fritext__" marker
      this.pdf_agreement = _.cloneDeep(this.updateInfo);

      // Detect if payment_terms is freetext (doesn't match standard options)
      this.initializeFreetextPaymentTerms();

      this.selected_pdf_file = null;
      this.remove_pdf_file = false;
      this.customerSearchQuery = '';
      if (newVal && newVal.status === 'temporary') {
        this.getCustomers();
      }
    },
    "$i18n.locale"(newLocale) {
      this.date_picker_lang = newLocale;
      this.date_picker_langKey++;
    },
  },
  methods: {
    initializeFreetextPaymentTerms() {
      if (!this.updateInfo || !this.updateInfo.payment_terms) return;
      
      // Standard payment terms values (both Norwegian and Swedish)
      const standardOptions = [
        'Årlig', 'Halvårlig', 'Kvartalsvis', 'Månedlig', '__fritekst__',  // Norwegian
        'Årsvis', 'Halv år', 'Kvartal', 'Månad', '__fritext__'  // Swedish
      ];
      
      const isFreetextMarker = this.updateInfo.payment_terms === '__fritekst__' || this.updateInfo.payment_terms === '__fritext__';
      
      // Case 1: payment_terms is already a freetext marker - ensure freetext field is initialized with $set for reactivity
      if (isFreetextMarker) {
        if (!this.updateInfo.payment_terms_freetext) {
          this.$set(this.updateInfo, 'payment_terms_freetext', '');
        }
        return;
      }
      
      // Case 2: payment_terms is not a standard option - it's a freetext value saved directly
      if (!standardOptions.includes(this.updateInfo.payment_terms)) {
        const isNorwegian = this.agreementCountry() === 'no';
        // Store the original payment_terms value as freetext (use $set for reactivity)
        this.$set(this.updateInfo, 'payment_terms_freetext', this.updateInfo.payment_terms);
        // Set the dropdown to the freetext marker
        this.updateInfo.payment_terms = isNorwegian ? '__fritekst__' : '__fritext__';
      }
    },
    // Everything country-bound in the form (product catalog, sellers, alarm
    // centers, customers, payment terms) follows the agreement's country, not
    // the portal's - a Norwegian agreement opened from the Swedish portal is
    // edited against the Norwegian catalog and lists.
    agreementCountry() {
      return (this.updateInfo && this.updateInfo.country) || this.$options.siteContext.getSiteContext();
    },
    loadCountryLists() {
      this.listsCountry = this.agreementCountry();
      this.getSellers();
      this.getAlarmCenters();
      this.fetchSubcategories();
    },
    getSellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/sellers/`, { params: { country: this.agreementCountry() } })
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },
    async fetchSubcategories() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/subcategories`, { params: { country: this.agreementCountry() } });
        this.subcategories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      }
    },
    // Get products that belong to a specific subcategory within a category
    getProductsForSubcategory(categoryValue, subcategoryId) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => {
        if (!p.subcategory) return false;
        const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
        return String(productSubcatId) === String(subcategoryId);
      });
    },
    // Count products with qty > 0 in a category (including variants)
    categoryProductCount(categoryValue) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.reduce((count, p) => {
        if (p.variants && p.variants.length > 0) {
          return count + p.variants.filter(v => (v.qty || 0) > 0).length;
        }
        return count + ((p.qty || 0) > 0 ? 1 : 0);
      }, 0);
    },
    // Count products with qty > 0 in a subcategory (including variants)
    subcategoryProductCount(categoryValue, subcategoryId) {
      const products = this.getProductsForSubcategory(categoryValue, subcategoryId);
      return products.reduce((count, p) => {
        if (p.variants && p.variants.length > 0) {
          return count + p.variants.filter(v => (v.qty || 0) > 0).length;
        }
        return count + ((p.qty || 0) > 0 ? 1 : 0);
      }, 0);
    },
    // Get products without any subcategory within a category
    getProductsWithoutSubcategory(categoryValue) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => !p.subcategory);
    },
    // Toggle category expansion
    toggleCategory(categoryValue) {
      this.$set(this.expandedCategories, categoryValue, !this.expandedCategories[categoryValue]);
    },
    // Toggle subcategory expansion
    toggleSubcategory(subcategoryId) {
      this.$set(this.expandedSubcategories, subcategoryId, !this.expandedSubcategories[subcategoryId]);
    },
    // Toggle product variants expansion
    toggleVariants(productId) {
      this.$set(this.expandedVariants, productId, !this.expandedVariants[productId]);
    },
    // Get category label from value
    getCategoryLabel(categoryValue) {
      const cat = this.categoryOptions.find(c => c.value === categoryValue);
      return cat ? cat.label : categoryValue;
    },
    calculateTotalPrice(){
      let total = 0;
      const category = this.updateInfo.agreement_category;
      
      this.updateInfo.products.forEach(p => {
        const isCashProduct = (p.category === 'accessory' || p.category === 'other') && p.type && p.type.includes('cash');
        const isMonthlyOrSoftware = p.is_software || p.category === 'simcard' || p.category === 'software' ||
          (p.category === 'accessory' && p.type && p.type.includes('monthly')) ||
          (p.category === 'other' && p.type && p.type.includes('monthly'));

        // Determine effective qty: sum variant quantities when variants are active, otherwise use parent qty
        const activeVariants = (p.variants || []).filter(v => (v.qty || 0) > 0);
        const effectiveQty = activeVariants.length > 0
          ? activeVariants.reduce((sum, v) => sum + (v.qty || 0), 0)
          : (p.qty || 0);
        const price = p.rental_price || p.price || 0;

        if (category === 'cash_services') {
          if (isMonthlyOrSoftware) {
            total += effectiveQty * price;
          }
        } else {
          if (!isCashProduct) {
            total += effectiveQty * price;
          }
        }
      });
      this.updateInfo.rental_month_cost = total;
    },
    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
        evt.preventDefault();
      }
    },
    getCustomers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer`, { params: { country: this.agreementCountry() } })
        .then((res) => { this.customers = res.data.data; })
        .catch(this.handleError);
    },

    selectCustomerEdit(customer) {
      this.updateInfo.customer_id = customer._id;
      this.customerSearchQuery = '';
      this.updateInfo.customer_name = customer.name;
      this.updateInfo.company_number = customer.company_number;
      this.updateInfo.customer_phone = customer.phone;
      this.updateInfo.billing_adress = customer.billing_address;
      this.updateInfo.billing_zip_code = customer.billing_zip_code;
      this.updateInfo.billing_city = customer.billing_city;
      this.updateInfo.customer_contact = customer.contact_name;
      this.updateInfo.customer_reference = customer.billing_reference;
      this.updateInfo.e_bill = customer.billing_invoice || this.updateInfo.e_bill;
    },

    clearCustomerSelectionEdit() {
      this.updateInfo.customer_id = '';
      this.customerSearchQuery = '';
    },

    getAlarmCenters() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central`, { params: { country: this.agreementCountry() } })
        .then((response) => {
          this.alarm_centers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    updateAgreement() {
      this.$refs.formUpdateAgreement.validate().then((success) => {
        if (!success) {
          return;
        }

        let headers = {
          "Content-Type": "multipart/form-data",
          "X-Access-Token": this.$store.state.token,
        };

        let formData = new FormData();

        // Calculate cash_sum based on agreement_category
        let cash_sum = '';
        if (this.updateInfo.agreement_category === 'rental' || this.updateInfo.agreement_category === 'financed' || this.updateInfo.agreement_category === 'financed_umea' || this.updateInfo.agreement_category === 'financed_ikano') {
          cash_sum = this.cashSumForRentalFinanced.toString();
        } else if (this.updateInfo.agreement_category === 'cash_services') {
          cash_sum = this.totalCashPrice.toString();
        }

        let rental_month_cost = this.updateInfo.rental_month_cost;

        formData.append("e_bill", this.updateInfo.e_bill);
        formData.append("customer_name", this.updateInfo.customer_name);
        formData.append("company_number", this.updateInfo.company_number);
        formData.append("customer_phone", this.updateInfo.customer_phone);
        formData.append("billing_adress", this.updateInfo.billing_adress);
        formData.append("billing_zip_code", this.updateInfo.billing_zip_code);
        formData.append("billing_city", this.updateInfo.billing_city);
        formData.append("customer_contact", this.updateInfo.customer_contact);
        formData.append("customer_reference", this.updateInfo.customer_reference);
        formData.append("dealer_name", this.updateInfo.dealer_name);
        formData.append("dealer_company_number", this.updateInfo.dealer_company_number);
        formData.append("dealer_phone", this.updateInfo.dealer_phone);
        formData.append("dealer_address", this.updateInfo.dealer_address);
        formData.append("dealer_zip_code", this.updateInfo.dealer_zip_code);
        formData.append("dealer_city", this.updateInfo.dealer_city);
        formData.append("seller", this.updateInfo.seller);
        formData.append("units_specification", this.updateInfo.units_specification);
        formData.append("contract_start", this.updateInfo.contract_start);
        formData.append("contract_period", this.updateInfo.contract_period);
        formData.append("setup_cost", this.updateInfo.setup_cost);
        formData.append("rental_month_cost", rental_month_cost);
        formData.append("cash_sum", cash_sum);
        // If freetext is selected, send the freetext value instead of the marker
        const paymentTermsValue = this.isPaymentTermsFreetext 
          ? this.updateInfo.payment_terms_freetext 
          : this.updateInfo.payment_terms;
        formData.append("payment_terms", paymentTermsValue);
        formData.append("sim_card", this.updateInfo.sim_card);
        formData.append("alarm_center", this.updateInfo.alarm_center);
        formData.append("special_terms", this.updateInfo.special_terms);
        formData.append("agreement_category", this.updateInfo.agreement_category);
        formData.append("insurance", this.updateInfo.insurance === true || this.updateInfo.insurance === 'yes');
        // Only store products with actual quantities, refreshing purchase_price from catalog
        const productsToSave = this.updateInfo.products
          .map(p => {
            const dbProd = this.productsFromDB
              ? (this.productsFromDB.find(db => p._id && String(db._id) === String(p._id)) ||
                 this.productsFromDB.find(db => p.type && db.type === p.type))
              : null;
            const freshPurchasePrice = dbProd ? (dbProd.purchase_price || 0) : (p.purchase_price || 0);

            if (p.variants && p.variants.length > 0) {
              const filteredVariants = p.variants.filter(v => (v.qty || 0) > 0).map(v => {
                const dbVariant = dbProd && dbProd.variants
                  ? dbProd.variants.find(dv => String(dv._id) === String(v._id) || dv.name === v.name)
                  : null;
                return { ...v, purchase_price: dbVariant ? (dbVariant.purchase_price ?? freshPurchasePrice) : (v.purchase_price || freshPurchasePrice) };
              });
              if (filteredVariants.length > 0) {
                return { ...p, variants: filteredVariants, purchase_price: freshPurchasePrice };
              }
            }
            if ((p.qty || 0) > 0) return { ...p, purchase_price: freshPurchasePrice };
            return null;
          })
          .filter(Boolean);
        formData.append("products", JSON.stringify(productsToSave));
        if (this.updateInfo.customer_id) formData.append("customer_id", this.updateInfo.customer_id);
      formData.append("software_service_cost", this.computeSoftwareServiceCost(this.updateInfo, Number(this.updateInfo.contract_period) || 0));
      formData.append("hardware_cost", this.computeHardwareCost(this.updateInfo));
        
        //formData.append('pdf_hide_alarmcenter', this.updateInfo.pdf_hide_alarmcenter);
        if (this.selected_pdf_file) formData.append("pdf", this.selected_pdf_file);
        if (this.remove_pdf_file) formData.append("remove_pdf_file", this.remove_pdf_file);

        this.axios
          .put(`${process.env.VUE_APP_SERVER_URL}/agreements/${this.updateInfo._id}`, formData, headers)
          .then(() => {
            // Sync commission if one exists for this agreement
            if (this.updateInfo.agreement_number) {
              this.axios.put(
                `${process.env.VUE_APP_SERVER_URL}/sales/commissions/sync-from-agreement/${encodeURIComponent(this.updateInfo.agreement_number)}`
              ).catch(err => console.error('Commission sync failed:', err));
            }
            // eslint-disable-line
            this.$emit("updateAgreement");
            this.$modal.hide("update-agreement");
          })
          .catch((error) => {
            this.handleError(error);
          });
      });
    },

    fileChangeCreate() {
      if (this.$refs.pdfCreate.files.length > 0) this.selected_pdf_file = this.$refs.pdfCreate.files[0];
      else this.selected_pdf_file = null;
    },

    downloadPDF(agreement) {
      let doc = this.generateAgreementPDF(agreement);
      doc.save(`${agreement.agreement_number}-${agreement.customer_name}.pdf`);
    },

    removePDF() {
      this.updateInfo.pdf_file = null;
      this.remove_pdf_file = true;
    },

    async replacePdf(pdfType, index, event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('pdf_type', pdfType);
      formData.append('index', index);

      try {
        const response = await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreement/replace-pdf/${this.updateInfo._id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Access-Token': this.$store.state.token,
            },
          }
        );

        const newUrl = response.data.pdf_url;

        if (pdfType === 'insurance_removal') {
          this.$set(this.updateInfo, 'insurance_removal_pdf', newUrl);
        } else if (pdfType === 'cancellation') {
          this.$set(this.updateInfo.cancellation_info[index], 'cancellation_pdf', newUrl);
        } else if (pdfType === 'dismissal') {
          this.$set(this.updateInfo.dismissal_info[index], 'dismissal_pdf', newUrl);
        }

        this.$emit('updateAgreement');
      } catch (error) {
        this.handleError(error);
      }

      event.target.value = '';
    },

    getSellerName(id) {
      let seller = _.find(this.sellers, { _id: id });
      if (seller) return seller.name;
      else return "";
    },

    hide(id) {
      this.expandedCategories = {};
      this.expandedSubcategories = {};
      this.expandedVariants = {};
      this.$modal.hide(id);
    },

    fetchProductsFromOrder(agreementNumber) {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/admin-order?agreement_number=${agreementNumber}`)
        .then((response) => {
          if (response.data.data && response.data.data.length > 0) {
            const orderProducts = response.data.data[0].products;
            this.updateInfo.products = this.mergeProducts(orderProducts);
            // Pending/temporary: show full catalog so sellers can add products
            const editableStatuses = ['temporary', 'pending'];
            if (editableStatuses.includes(this.updateInfo.status)) {
              this.updateInfo.products = this.addMissingCatalogProducts(this.updateInfo.products);
            }
          } else {
            // No order found, use all products from DB with zero quantities
            this.updateInfo.products = this.productsFromDB.map(p => ({
              _id: p._id,
              display_name: p.display_name || null,
              name: p.name,
              type: p.type,
              category: p.category || 'other',
              subcategory: p.subcategory || null,
              hasVariants: p.hasVariants || false,
              variants: (p.variants || []).map(v => ({
                ...v,
                qty: 0,
                rental_price: null,
                cash_price: null,
                recommended_rental_price: v.recommended_rental_price || 0,
                recommended_cash_price: v.recommended_cash_price || 0
              })),
              qty: 0,
              rental_price: null,
              cash_price: null,
              is_software: p.is_software || false,
              always_visible: p.always_visible || false,
              recommended_rental_price: p.recommended_rental_price || 0,
              recommended_cash_price: p.recommended_cash_price || 0
            }));
          }
        })
        .catch((error) => {
          console.error('Error fetching order products:', error);
          // Fallback to products from DB
          this.updateInfo.products = this.productsFromDB.map(p => ({
            _id: p._id,
            display_name: p.display_name || null,
            name: p.name,
            type: p.type,
            category: p.category || 'other',
            subcategory: p.subcategory || null,
            hasVariants: p.hasVariants || false,
            variants: (p.variants || []).map(v => ({
              ...v,
              qty: 0,
              rental_price: null,
              cash_price: null,
              recommended_rental_price: v.recommended_rental_price || 0,
              recommended_cash_price: v.recommended_cash_price || 0
            })),
            qty: 0,
            rental_price: null,
            cash_price: null,
            is_software: p.is_software || false,
            always_visible: p.always_visible || false,
            recommended_rental_price: p.recommended_rental_price || 0,
            recommended_cash_price: p.recommended_cash_price || 0
          }));
        });
    },

    async fetchProductsFromDB() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`, { params: { country: this.agreementCountry() } });
        this.productsFromDB = response.data.data;
      } catch (error) {
        console.error('Failed to fetch products from DB:', error);
        this.productsFromDB = [];
      }
    },

    addMissingCatalogProducts(existingProducts) {
      // Collect flat variant entries keyed by parent _id
      const flatVariantsByParent = {};
      existingProducts.forEach(p => {
        if (p.variant_id && p._id) {
          const parentId = String(p._id);
          if (!flatVariantsByParent[parentId]) flatVariantsByParent[parentId] = [];
          flatVariantsByParent[parentId].push(p);
        }
      });

      // Remove flat variant entries — they'll be folded into the parent product
      const nonFlatProducts = existingProducts.filter(p => !p.variant_id);
      const existingIds = new Set(nonFlatProducts.map(p => String(p._id)).filter(Boolean));

      const catalog = this.productsFromDB.map(dbP => {
        const dbId = String(dbP._id);
        const flatVariants = flatVariantsByParent[dbId];

        if (existingIds.has(dbId)) {
          // Product already exists as a non-flat entry — keep as-is, skip catalog version
          return null;
        }

        // Build the catalog product with qty from existing data
        const existingNonFlat = nonFlatProducts.find(p => String(p._id) === dbId);

        return {
          _id: dbP._id,
          display_name: dbP.display_name || null,
          name: dbP.name,
          type: dbP.type,
          category: dbP.category || 'other',
          subcategory: dbP.subcategory || null,
          hasVariants: dbP.hasVariants || false,
          variants: (dbP.variants || []).map(v => {
            // Transfer qty/price from matching flat variant if it exists
            const match = flatVariants && flatVariants.find(fv =>
              (fv.variant_id && v._id && String(fv.variant_id) === String(v._id)) ||
              (fv.variant_name && v.name && fv.variant_name === v.name)
            );
            return {
              ...v,
              qty: match ? (match.qty || 0) : 0,
              rental_price: match ? (match.rental_price || match.price || null) : null,
              cash_price: match ? (match.cash_price || null) : null,
              recommended_rental_price: v.recommended_rental_price || 0,
              recommended_cash_price: v.recommended_cash_price || 0
            };
          }),
          qty: existingNonFlat ? (existingNonFlat.qty || 0) : 0,
          rental_price: existingNonFlat ? (existingNonFlat.rental_price || null) : null,
          cash_price: existingNonFlat ? (existingNonFlat.cash_price || null) : null,
          is_software: dbP.is_software || false,
          always_visible: dbP.always_visible || false,
          purchase_price: dbP.purchase_price || 0,
          recommended_rental_price: dbP.recommended_rental_price || 0,
          recommended_cash_price: dbP.recommended_cash_price || 0
        };
      }).filter(Boolean);

      // Combine: keep existing non-flat products, then add catalog products (new + reconstructed from flat variants)
      return [...nonFlatProducts, ...catalog];
    },

    mergeProducts(existingProducts) {
      // Simple approach: enrich existing products with DB data (subcategory, recommended prices, etc.)
      const enriched = existingProducts.map(p => {
        // Find matching product in DB
        const dbProduct = this.productsFromDB.find(dbP => 
          (p._id && dbP._id && String(p._id) === String(dbP._id)) ||
          (p.type && dbP.type && p.type === dbP.type)
        );

        if (!dbProduct) {
          // No DB match, return product as-is with minimal cleanup
          return { 
            ...p, 
            rental_price: p.rental_price || p.price || null,
            qty: p.qty || 0,
            category: p.category || 'other'
          };
        }

        // Flat variant product: already represents a specific variant, don't inherit parent variant structure
        const isFlatVariant = !!(p.variant_id || p.variant_name);

        // Merge DB data with existing product
        return {
          ...p,
          // Preserve existing qty and prices from agreement
          rental_price: p.rental_price || p.price || null,
          qty: p.qty || 0,
          // Always refresh purchase_price from current catalog (embedded value may be stale)
          purchase_price: dbProduct.purchase_price || 0,
          // Restore missing fields from DB
          subcategory: p.subcategory || dbProduct.subcategory || null,
          category: p.category || dbProduct.category || 'other',
          _id: p._id || dbProduct._id,
          display_name: p.display_name || dbProduct.display_name || null,
          is_software: p.is_software !== undefined ? p.is_software : (dbProduct.is_software || false),
          always_visible: p.always_visible !== undefined ? p.always_visible : (dbProduct.always_visible || false),
          // Flat variant products should not show variant picker — they ARE a specific variant
          hasVariants: isFlatVariant ? false : (p.hasVariants !== undefined ? p.hasVariants : (dbProduct.hasVariants || false)),
          // Ensure variants have recommended prices for placeholders
          variants: isFlatVariant ? [] : (p.variants || []).map(v => ({
            ...v,
            qty: v.qty || 0,
            rental_price: v.rental_price || v.price || null,
            recommended_rental_price: v.recommended_rental_price || 0,
            recommended_cash_price: v.recommended_cash_price || 0
          }))
        };
      });

      // Deduplicate — use composite key including variant_id for flat variant products
      // so two variants of the same parent product aren't collapsed
      const seen = new Set();
      return enriched.filter(p => {
        const idPart = p._id ? String(p._id) : null;
        if (!idPart) return true;
        const variantPart = p.variant_id ? String(p.variant_id) : '';
        const key = variantPart ? `${idPart}-${variantPart}` : idPart;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    shouldShowRentalForProduct(product) {
      const category = this.updateInfo.agreement_category;
      
      // For rental and financed: show rental for all products except cash products
      if (category === 'rental' || category === 'financed' || category === 'financed_umea' || category === 'financed_ikano') {
        // Hide rental price for cash-only products (accessory/other with 'cash' in type)
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) {
          return false; // These use cash_price
        }
        return true;
      }
      
      // For cash_services: show rental for software and monthly products
      if (category === 'cash_services') {
        if (product.is_software === true) return true;
        if (product.category === 'simcard' || product.category === 'software') return true;
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('monthly')) return true;
        return false;
      }
      
      return false;
    },
    
    shouldShowCashForProduct(product) {
      const category = this.updateInfo.agreement_category;
      
      // For rental and financed: only show cash for cash products (accessory/other with 'cash' in type)
      if (category === 'rental' || category === 'financed' || category === 'financed_umea' || category === 'financed_ikano') {
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) {
          return true;
        }
        return false;
      }
      
      // For cash_services: show cash for hardware and cash products (not software/monthly)
      if (category === 'cash_services') {
        // Show cash for cash products
        if ((product.category === 'accessory' || product.category === 'other') && 
            product.type && product.type.includes('cash')) return true;
        // Show cash for hardware (not software, not simcard, not monthly)
        if (!product.is_software && product.category !== 'simcard' && product.category !== 'software' &&
            !((product.category === 'accessory' || product.category === 'other') && 
              product.type && product.type.includes('monthly'))) return true;
        return false;
      }
      
      return false;
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    if (this.user && /(^admin$)|(^seller$)/.test(this.user.role)) {
      this.loadCountryLists();
    }
  },
};
</script>
